import { ApolloError, useApolloClient } from '@apollo/client';
import { remapApolloErrors } from 'helpers/remapApolloErrors';
import { showToast } from 'helpers/showToast';
import { DeleteAutoImportMutationOptions, PlanUsageType, UsageFieldsFragmentDoc } from 'models/graphql';
import { AppContext } from 'providers/AppProvider';
import React, { useCallback, useContext, useState } from 'react';
import { DeleteImporterModalView, DeleteImporterModalViewProps } from './DeleteImporterModal.view';

export type DeleteImporterModalControllerProps = Omit<DeleteImporterModalViewProps, 'onConfirmation' | 'loading'> & {
  deleteImport: (options: DeleteAutoImportMutationOptions) => Promise<void>;
  deleteAllImports: () => Promise<void>;
  id: string | undefined;
};

export const DeleteImporterModalController = ({
  onClose,
  importerTitle,
  id,
  deleteImport,
  deleteAllImports
}: DeleteImporterModalControllerProps) => {
  const { cache } = useApolloClient();
  const [loading, setLoading] = useState(false);
  const { selectedWorkspaceId } = useContext(AppContext);

  const updateCache = useCallback(
    (deleteAll: boolean) => {
      const oldUsage: PlanUsageType | null = cache.readFragment({
        fragment: UsageFieldsFragmentDoc,
        fragmentName: 'UsageFields',
        id: 'PlanUsageType:{}'
      });
      const newUsage = { ...oldUsage, autoImports: deleteAll ? 0 : (oldUsage?.autoImports ?? 0) - 1 };
      cache.writeFragment({
        fragment: UsageFieldsFragmentDoc,
        fragmentName: 'UsageFields',
        id: 'PlanUsageType:{}',
        data: newUsage
      });
    },
    [cache]
  );

  const onConfirmation = useCallback(async () => {
    setLoading(true);

    if (id) {
      try {
        await deleteImport({
          variables: { importId: id }
        });
        onClose();
        updateCache(false);
      } catch (e) {
        const error = e as ApolloError;
        const mappedError = remapApolloErrors(error);
        showToast({
          summary: 'Error in deleting the Auto Importer',
          detail: mappedError,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    } else if (selectedWorkspaceId) {
      try {
        await deleteAllImports();
        updateCache(true);
        onClose();
      } catch (e) {
        const error = e as ApolloError;
        const mappedError = remapApolloErrors(error);
        showToast({
          summary: 'Error in deleting the Auto Importers',
          detail: mappedError,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  }, [id, deleteImport, onClose, selectedWorkspaceId, deleteAllImports, updateCache]);

  const props: DeleteImporterModalViewProps = { onClose, onConfirmation, importerTitle, loading };
  return <DeleteImporterModalView {...props}></DeleteImporterModalView>;
};
